import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getImagePath } from '../model/yogaModel';

const getDefaultShowAllColumns = () => (
  window.matchMedia('(max-width: 768px)').matches ? 2 : 5
);

const getMaximumShowAllColumns = () => (
  window.matchMedia('(max-width: 768px)').matches ? 2 : 8
);

const MINIMUM_SEQUENCE_STROKE_WIDTH = 160;
const POSE_STROKE_SPACING = 11;

function PoseNote({ note }) {
  if (!note) return null;

  return (
    <p
      className="sequence-pose-note"
      style={{
        '--pose-note-background': note.backgroundColor,
        '--pose-note-color': note.textColor,
      }}
    >
      {note.text}
    </p>
  );
}

function SequencePageView({ activeSequence, imageSources, selected, renderHeaderOptions, onGoHome, onStartSequence }) {
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const [showAllZoom, setShowAllZoom] = useState(getDefaultShowAllColumns);
  const [showManualHint, setShowManualHint] = useState(false);
  const pendingSequenceIndex = useRef(null);
  const lastScrollSwitchTime = useRef(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const strokeTouchStartX = useRef(null);
  const strokeTouchStartY = useRef(null);
  const strokePointerStartX = useRef(null);
  const strokePointerScrollLeft = useRef(0);
  const isStrokeDragging = useRef(false);
  const suppressStrokeClick = useRef(false);
  const sequenceStrokesRef = useRef(null);
  const poseStrokeRefs = useRef([]);
  const strokeScrollFrame = useRef(null);

  const showAllPoses = selected.display === 'SHOW ALL';
  const isManualMode = selected.mode === 'MANUAL';
  const isAutoSequenceMode = selected.display === 'SHOW IN SEQUENCE' && selected.mode === 'AUTO';
  const showSequenceMap = selected.display === 'SHOW IN SEQUENCE' && isManualMode;
  const currentFrame = activeSequence.sequenceFrames[sequenceIndex];
  const currentPoseNote = activeSequence.poseNotes?.[currentFrame];
  const previousFrame = activeSequence.sequenceFrames[sequenceIndex - 1];
  const nextFrame = activeSequence.sequenceFrames[sequenceIndex + 1];
  const hasReachedEnd = !showAllPoses && sequenceIndex >= activeSequence.sequenceFrames.length;
  const maximumShowAllColumns = getMaximumShowAllColumns();

  const findPreviousAvailableSequence = useCallback(() => {
    const currentIndex = imageSources.findIndex((sequence) => sequence.id === activeSequence.id);

    return imageSources
      .slice(0, currentIndex)
      .reverse()
      .find((sequence) => sequence.hasSequence);
  }, [activeSequence.id, imageSources]);

  // Reset the pose position when the active sequence or display mode changes.
  useEffect(() => {
    const nextIndex = pendingSequenceIndex.current ?? 0;
    pendingSequenceIndex.current = null;
    setSequenceIndex(nextIndex);
  }, [activeSequence, selected.display]);

  // Cancel any pending progress-map animation frame when the view unmounts.
  useEffect(() => () => {
    if (strokeScrollFrame.current) {
      window.cancelAnimationFrame(strokeScrollFrame.current);
    }
  }, []);

  // Keep the active pose marker centered in the manual progress map.
  useEffect(() => {
    const container = sequenceStrokesRef.current;
    if (!container || !showSequenceMap) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const activeStroke = poseStrokeRefs.current.find((entry) => (
        entry?.sequence.id === activeSequence.id && entry.poseIndex === sequenceIndex
      ));

      if (!activeStroke?.el) return;

      const containerRect = container.getBoundingClientRect();
      const strokeRect = activeStroke.el.getBoundingClientRect();
      const strokeCenter = strokeRect.left - containerRect.left + container.scrollLeft + strokeRect.width / 2;

      container.scrollTo({
        left: strokeCenter - containerRect.width / 2,
        behavior: 'auto',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeSequence.id, sequenceIndex, showSequenceMap]);

  // Restore the default zoom whenever a sequence opens in the all-poses view.
  useEffect(() => {
    if (selected.display === 'SHOW ALL') {
      setShowAllZoom(getDefaultShowAllColumns());
    }
  }, [activeSequence, selected.display]);

  // Briefly show keyboard instructions when manual sequence mode becomes active.
  useEffect(() => {
    if (!showSequenceMap) {
      setShowManualHint(false);
      return undefined;
    }

    setShowManualHint(true);

    const timer = setTimeout(() => {
      setShowManualHint(false);
    }, 4200);

    return () => clearTimeout(timer);
  }, [activeSequence.id, showSequenceMap]);

  // Continue to the next available sequence, or return home after the final one.
  useEffect(() => {
    if (!hasReachedEnd) return;

    const currentIndex = imageSources.findIndex((sequence) => sequence.id === activeSequence.id);
    const nextAvailableSequence = imageSources
      .slice(currentIndex + 1)
      .find((sequence) => sequence.hasSequence);

    if (nextAvailableSequence) {
      setSequenceIndex(0);
      onStartSequence(nextAvailableSequence);
      return;
    }

    onGoHome();
  }, [activeSequence.id, hasReachedEnd, imageSources, onGoHome, onStartSequence]);

  // Advance to the next pose every minute while automatic mode is active.
  useEffect(() => {
    if (selected.display !== 'SHOW IN SEQUENCE') return undefined;
    if (selected.mode !== 'AUTO') return undefined;
    if (sequenceIndex >= activeSequence.sequenceFrames.length) return undefined;

    const timer = setTimeout(() => {
      setSequenceIndex((currentIndex) => (
        Math.min(currentIndex + 1, activeSequence.sequenceFrames.length)
      ));
    }, 60000);

    return () => clearTimeout(timer);
  }, [activeSequence, selected.display, selected.mode, sequenceIndex]);

  // Register keyboard navigation for manual mode and remove it on cleanup.
  useEffect(() => {
    if (selected.display !== 'SHOW IN SEQUENCE') return undefined;
    if (selected.mode !== 'MANUAL') return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setSequenceIndex((currentIndex) => (
          Math.min(currentIndex + 1, activeSequence.sequenceFrames.length)
        ));
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setSequenceIndex((currentIndex) => {
          if (currentIndex > 0) {
            return currentIndex - 1;
          }

          const previousSequence = findPreviousAvailableSequence();

          if (previousSequence) {
            pendingSequenceIndex.current = previousSequence.sequenceFrames.length - 1;
            onStartSequence(previousSequence);
          }

          return currentIndex;
        });
      }

      if (event.key === 'Escape') {
        onGoHome();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSequence, findPreviousAvailableSequence, imageSources, onGoHome, onStartSequence, selected.display, selected.mode]);

  const handleSequenceStrokeClick = (sequence) => {
    if (!sequence.hasSequence) return;

    pendingSequenceIndex.current = 0;
    setSequenceIndex(0);
    onStartSequence(sequence);
  };

  const handlePoseStrokeClick = (event, sequence, poseIndex) => {
    if (suppressStrokeClick.current) return;
    if (!sequence.hasSequence) return;

    event.currentTarget.blur();

    if (sequence.id === activeSequence.id) {
      pendingSequenceIndex.current = null;
      setSequenceIndex(poseIndex);
      return;
    }

    pendingSequenceIndex.current = poseIndex;
    onStartSequence(sequence);
  };

  const handlePreviousPoseClick = () => {
    if (!isManualMode || !previousFrame) return;

    setSequenceIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  };

  const handleNextPoseClick = () => {
    if (!isManualMode || !nextFrame) return;

    setSequenceIndex((currentIndex) => (
      Math.min(currentIndex + 1, activeSequence.sequenceFrames.length - 1)
    ));
  };

  const switchManualPoseByScroll = (direction) => {
    if (selected.display !== 'SHOW IN SEQUENCE') return;
    if (!isManualMode) return;
    if (window.innerWidth > 768) return;

    if (direction > 0 && sequenceIndex === activeSequence.sequenceFrames.length - 1) {
      onGoHome();
      return;
    }

    if (direction < 0 && sequenceIndex === 0) {
      const previousSequence = findPreviousAvailableSequence();

      if (previousSequence) {
        pendingSequenceIndex.current = previousSequence.sequenceFrames.length - 1;
        onStartSequence(previousSequence);
      }

      return;
    }

    setSequenceIndex((currentIndex) => {
      if (direction > 0) {
        return Math.min(currentIndex + 1, activeSequence.sequenceFrames.length - 1);
      }

      return Math.max(currentIndex - 1, 0);
    });
  };

  const handleSequenceWheel = (event) => {
    if (Math.abs(event.deltaY) < 20) return;
    event.preventDefault();

    const now = Date.now();
    if (now - lastScrollSwitchTime.current < 450) return;

    lastScrollSwitchTime.current = now;
    switchManualPoseByScroll(event.deltaY > 0 ? 1 : -1);
  };

  const handleSequenceTouchStart = (event) => {
    const touch = event.touches[0];

    touchStartX.current = touch?.clientX ?? null;
    touchStartY.current = touch?.clientY ?? null;
  };

  const handleSequenceTouchEnd = (event) => {
    if (touchStartX.current === null || touchStartY.current === null) return;

    const touch = event.changedTouches[0];
    const endX = touch?.clientX ?? touchStartX.current;
    const endY = touch?.clientY ?? touchStartY.current;
    const deltaX = touchStartX.current - endX;
    const deltaY = touchStartY.current - endY;
    touchStartX.current = null;
    touchStartY.current = null;

    const horizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const swipeDistance = horizontalSwipe ? deltaX : deltaY;

    if (Math.abs(swipeDistance) < 35) return;

    switchManualPoseByScroll(swipeDistance > 0 ? 1 : -1);
  };

  const activateCenteredStroke = () => {
    const container = sequenceStrokesRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const centerPosition = container.scrollLeft + containerRect.width / 2;
    let closest = null;
    let closestDistance = Infinity;

    poseStrokeRefs.current.forEach((entry) => {
      if (!entry?.el) return;

      const rect = entry.el.getBoundingClientRect();
      const strokeCenter = rect.left - containerRect.left + container.scrollLeft + rect.width / 2;
      const distance = Math.abs(strokeCenter - centerPosition);

      if (distance < closestDistance) {
        closestDistance = distance;
        closest = entry;
      }
    });

    if (!closest) return;

    if (closest.sequence.id === activeSequence.id) {
      if (closest.poseIndex === sequenceIndex) return;
      setSequenceIndex(closest.poseIndex);
      return;
    }

    pendingSequenceIndex.current = closest.poseIndex;
    onStartSequence(closest.sequence);
  };

  const registerPoseStroke = (el, sequence, poseIndex) => {
    if (!el) return;

    poseStrokeRefs.current.push({ el, sequence, poseIndex });
  };

  const registerSequenceStroke = (el, sequence) => {
    if (!el || !sequence.hasSequence) return;

    poseStrokeRefs.current.push({ el, sequence, poseIndex: 0 });
  };

  const handleStrokesWheel = (event) => {
    const container = sequenceStrokesRef.current;
    if (!container) return;

    event.preventDefault();
    container.scrollLeft += event.deltaX || event.deltaY;
    activateCenteredStroke();
  };

  const handleStrokesTouchStart = (event) => {
    strokeTouchStartX.current = event.touches[0]?.clientX ?? null;
    strokeTouchStartY.current = event.touches[0]?.clientY ?? null;
  };

  const handleStrokesTouchMove = (event) => {
    const container = sequenceStrokesRef.current;
    if (!container || strokeTouchStartX.current === null) return;

    const currentX = event.touches[0]?.clientX ?? strokeTouchStartX.current;
    const currentY = event.touches[0]?.clientY ?? strokeTouchStartY.current;
    const deltaX = strokeTouchStartX.current - currentX;
    const deltaY = strokeTouchStartY.current === null ? 0 : strokeTouchStartY.current - currentY;

    container.scrollLeft += Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
    strokeTouchStartX.current = currentX;
    strokeTouchStartY.current = currentY;
    activateCenteredStroke();
  };

  const handleStrokesTouchEnd = () => {
    strokeTouchStartX.current = null;
    strokeTouchStartY.current = null;
    activateCenteredStroke();
  };

  const handleStrokesPointerDown = (event) => {
    const container = sequenceStrokesRef.current;
    if (!container) return;

    strokePointerStartX.current = event.clientX;
    strokePointerScrollLeft.current = container.scrollLeft;
    isStrokeDragging.current = false;
  };

  const handleStrokesPointerMove = (event) => {
    const container = sequenceStrokesRef.current;
    if (!container || strokePointerStartX.current === null) return;

    const deltaX = event.clientX - strokePointerStartX.current;
    if (Math.abs(deltaX) > 3) {
      if (!isStrokeDragging.current) {
        container.setPointerCapture?.(event.pointerId);
      }
      isStrokeDragging.current = true;
      suppressStrokeClick.current = true;
    }

    if (!isStrokeDragging.current) return;

    container.scrollLeft = strokePointerScrollLeft.current - deltaX;
    activateCenteredStroke();
  };

  const handleStrokesPointerUp = (event) => {
    const container = sequenceStrokesRef.current;

    const wasDragging = isStrokeDragging.current;

    if (wasDragging && container?.hasPointerCapture?.(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
    strokePointerStartX.current = null;
    if (wasDragging) activateCenteredStroke();

    window.setTimeout(() => {
      isStrokeDragging.current = false;
      suppressStrokeClick.current = false;
    }, 0);
  };

  poseStrokeRefs.current = [];

  return (
    <div className="App sequence-page">
      <header className="top-row">
        <div className="sequence-title-row">
          <h1 className="main-title main-title-link" onClick={onGoHome}>
            ASHTANGA YOGA
          </h1>
          {(showAllPoses || !isManualMode) && (
            <span className="sequence-name-label">{activeSequence.name}</span>
          )}
        </div>
        <div className="sequence-header-actions">
          {showAllPoses && (
            <div className="zoom-controls">
              <button
                className="zoom-button"
                type="button"
                onClick={() => setShowAllZoom((zoom) => Math.max(zoom - 1, 1))}
                disabled={showAllZoom === 1}
                aria-label="Zoom in"
              >
                +
              </button>
              <button
                className="zoom-button"
                type="button"
                onClick={() => setShowAllZoom((zoom) => Math.min(zoom + 1, maximumShowAllColumns))}
                disabled={showAllZoom >= maximumShowAllColumns}
                aria-label="Zoom out"
              >
                -
              </button>
            </div>
          )}
          {renderHeaderOptions()}
        </div>
      </header>

      {showSequenceMap && showManualHint && (
        <div className="manual-sequence-hint">
          Press
          <span className="manual-key-group" aria-label="left and right arrows">
            <span className="manual-key">←</span>
            <span className="manual-key">→</span>
          </span>
          to navigate,
          <span className="manual-key">Esc</span>
          to exit
        </div>
      )}

      <section
        className={`sequence-view ${showAllPoses ? `show-all show-all-columns-${showAllZoom}` : 'show-one'} ${isAutoSequenceMode ? 'auto-sequence' : ''} ${showAllPoses && showAllZoom === 1 ? 'fit-full-image' : ''}`}
        onWheel={handleSequenceWheel}
        onTouchStart={handleSequenceTouchStart}
        onTouchEnd={handleSequenceTouchEnd}
        style={showAllPoses ? {
          '--show-all-columns': showAllZoom,
          '--show-all-image-width': `${100 / showAllZoom}%`,
          '--show-all-image-height': showAllZoom === 1 ? '100vh' : `${Math.max(24, 120 / showAllZoom)}vh`,
        } : undefined}
      >
        {showAllPoses && activeSequence.sequenceFrames.map((frame) => (
          <div key={frame} className="sequence-pose-tile">
            <div className="sequence-pose-visual">
              <img
                className="sequence-pose"
                src={getImagePath(
                  `${activeSequence.id}/${activeSequence.showAllDirectory || 'sequence'}/pose_${frame}.${activeSequence.showAllExtension || 'png'}`,
                )}
                alt={`${activeSequence.name} pose ${frame}`}
                loading="lazy"
                decoding="async"
              />
              <PoseNote note={activeSequence.poseNotes?.[frame]} />
            </div>
          </div>
        ))}

        {!showAllPoses && !hasReachedEnd && (
          <div className="sequence-focus-row">
            {previousFrame ? (
                <button
                  className="sequence-adjacent-pose sequence-adjacent-button"
                  type="button"
                  onClick={handlePreviousPoseClick}
                  disabled={!isManualMode}
                  aria-label="Show previous pose"
                >
                  <img
                    className="sequence-pose sequence-pose-preview"
                    src={getImagePath(`${activeSequence.id}/sequence/pose_${previousFrame}.png`)}
                    alt={`${activeSequence.name} previous pose ${previousFrame}`}
                  />
                </button>
            ) : (
              <div className="sequence-adjacent-pose" />
            )}

            <div className="sequence-current-pose-frame">
              <div className="sequence-current-pose-media">
                <img
                  key={currentFrame}
                  className="sequence-pose-current"
                  src={getImagePath(`${activeSequence.id}/sequence/pose_${currentFrame}.png`)}
                  alt={`${activeSequence.name} pose ${currentFrame}`}
                />
                <PoseNote note={currentPoseNote} />
              </div>
            </div>

            {nextFrame ? (
                <button
                  className="sequence-adjacent-pose sequence-adjacent-button"
                  type="button"
                  onClick={handleNextPoseClick}
                  disabled={!isManualMode}
                  aria-label="Show next pose"
                >
                  <img
                    className="sequence-pose sequence-pose-preview"
                    src={getImagePath(`${activeSequence.id}/sequence/pose_${nextFrame}.png`)}
                    alt={`${activeSequence.name} next pose ${nextFrame}`}
                  />
                </button>
            ) : (
              <div className="sequence-adjacent-pose" />
            )}
          </div>
        )}
      </section>

      {showSequenceMap && (
        <div
          ref={sequenceStrokesRef}
          className="sequence-strokes"
          aria-label="sequence progress"
          onWheel={handleStrokesWheel}
          onTouchStart={handleStrokesTouchStart}
          onTouchMove={handleStrokesTouchMove}
          onTouchEnd={handleStrokesTouchEnd}
          onPointerDown={handleStrokesPointerDown}
          onPointerMove={handleStrokesPointerMove}
          onPointerUp={handleStrokesPointerUp}
          onPointerCancel={handleStrokesPointerUp}
        >
          {imageSources.map((sequence) => {
            const isActiveSequence = sequence.id === activeSequence.id;

            return (
              <div
                key={sequence.id}
                className={`sequence-stroke-group ${sequence.hasSequence ? '' : 'disabled'} ${isActiveSequence ? 'active' : ''}`}
                style={{
                  '--sequence-stroke-width': `${Math.max(
                    MINIMUM_SEQUENCE_STROKE_WIDTH,
                    (sequence.sequenceFrames.length + 1) * POSE_STROKE_SPACING,
                  )}px`,
                }}
              >
                <button
                  ref={(el) => registerSequenceStroke(el, sequence)}
                  className="major-stroke-wrap"
                  type="button"
                  onClick={() => handleSequenceStrokeClick(sequence)}
                  disabled={!sequence.hasSequence}
                >
                  <span className="major-stroke" />
                  <span className="major-stroke-label">{sequence.name}</span>
                </button>

                <div className="minor-stroke-area">
                  {sequence.sequenceFrames.map((frame, index) => (
                    <button
                      key={frame}
                      ref={(el) => registerPoseStroke(el, sequence, index)}
                      type="button"
                      className={`minor-stroke ${isActiveSequence && index === sequenceIndex ? 'active' : ''}`}
                      onClick={(event) => handlePoseStrokeClick(event, sequence, index)}
                      aria-label={`${sequence.name} pose ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAutoSequenceMode && !hasReachedEnd && (
        <div className="sequence-timer-line" key={`${activeSequence.id}-${currentFrame}`} aria-hidden="true" />
      )}
    </div>
  );
}

export default SequencePageView;
